import React, { useEffect, useMemo, useState } from "react";
import apiService from "@/api/ApiService";
import { useQuery } from "@tanstack/react-query";

import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);

import { useGlobalStore, usePersistantStore } from "@/stores/global";
import { Box, Drawer, IconButton, Paper, Stack, Tooltip, Typography, ToggleButton, ToggleButtonGroup } from "@mui/material";
import {
  ArrowForward,
  AccessTime,
  EventAvailable,
  OpenInNew,
  Checklist,
  ChatBubbleOutline,
  LocalHospital,
  VerifiedUser,
  Healing,
  Warning,
} from "@mui/icons-material";

import { DataGridPro, GridColDef, useGridApiRef } from "@mui/x-data-grid-pro";
import { usePersistentDataGrid } from "@/shared/hooks/usePersistentDataGrid";
import useRights from "@/shared/api/useRights";

import { ChevronsUp, CircleDot, ChevronsDown } from "lucide-react";

import EventSidebar from "@/shared/sharedService/EventSidebar";
import { TopBar } from "@/shared/structure/TopBar";
import AddEvent from "./AddEvent";
import ContactName from "./ContactName";
import ApiService from "@/api/ApiService";
import { BobEvent } from "@/shared/sharedService/helpers";
import { useDeviceInfoModal } from "../device/DeviceInfoModal";

/**
 * =========================
 * Helpers / Types (inline)
 * =========================
 */

interface BinBase {
  key: "overdue" | "current" | "history";
  name: string;
  icon: React.ElementType;
}

export type BobEventMessageBase = {
  id: number;
  runningHours: {
    resource: number;
    id?: number;
    runningHours: number;
  } | null;
  format: "text" | "testing_json";
  message: string | EditedTestData[];
  attachments: any[];
};

export interface TestData {
  title: string;
  contents: string[];
}

export type EditedTestData = TestData & {
  comment: string;
  failed: boolean | null;
};

export type BinDefinition = BinBase & {
  rule: (event: BobEvent) => boolean;
};

export type Bin = BinBase & {
  events: BobEvent[];
};

export const getBinDefinitons = (role: "site" | "service"): BinDefinition[] => [
  {
    key: "overdue",
    name: "Überfällig",
    rule: (event: BobEvent) => !event.completedAt && event.assignedTo === role && !!event.dueAt && dayjs(event.dueAt, "YYYY-MM-DD") <= dayjs(),
    icon: AccessTime,
  },
  {
    key: "current",
    name: "Offen",
    rule: (event: BobEvent) => !event.completedAt,
    icon: EventAvailable,
  },
  {
    key: "history",
    name: "Historie",
    rule: (event: BobEvent) => !!event.completedAt,
    icon: EventAvailable,
  },
];

function parseDate(d: string | null, isDueAt = false): number {
  return d ? (isDueAt ? dayjs(d, "YYYY-MM-DD").valueOf() : dayjs(d).valueOf()) : Number.NEGATIVE_INFINITY;
}

function eventTimeMs(e: BobEvent): number {
  if (e.completedAt) return parseDate(e.completedAt);
  if (e.scheduledDate) return parseDate(e.scheduledDate, true);
  if (e.scheduledAt) return dayjs.utc(e.scheduledAt).valueOf();
  if (e.dueAt) return parseDate(e.dueAt, true);
  return parseDate(e.createdAt);
}

function toDateTimeValue(value: string | null | undefined): Date | null {
  if (!value) return null;
  const parsed = dayjs.utc(value).local();
  return parsed.isValid() ? parsed.toDate() : null;
}

function toDateOnlyValue(value: string | null | undefined): Date | null {
  if (!value) return null;
  const parsed = dayjs(value, "YYYY-MM-DD", true);
  return parsed.isValid() ? parsed.startOf("day").toDate() : null;
}

export function generateBins(events: BobEvent[], definitions: BinDefinition[]): Bin[] {
  const bins: Bin[] = definitions.map(({ key, name, icon }) => ({
    key,
    name,
    icon,
    events: [],
  }));

  const compareByFallbackTime = (a: BobEvent, b: BobEvent) => (!!a.completedAt ? eventTimeMs(b) - eventTimeMs(a) : eventTimeMs(a) - eventTimeMs(b));

  for (const ev of events) {
    if (!!ev.completedAt && !ev.categorySettings.showCompleted) continue;
    const idx = definitions.findIndex((def) => def.rule(ev));
    if (idx !== -1) bins[idx].events.push(ev);
  }

  for (const bin of bins) {
    bin.events.sort(compareByFallbackTime);
  }

  return bins.filter((b) => b.events.length > 0);
}

export function getSubtitle(event: BobEvent) {
  if (!!event.completedAt) {
    return ["Erledigt am " + dayjs(event.completedAt).format("ddd, DD.MM.YYYY"), event.completedAt];
  }

  let duePart = "";
  if (!!event.dueAtAmount) {
    duePart = "Fällig bei " + event.dueAtAmount + " h";
    if (!!event.dueAt) {
      duePart += " (ca. " + dayjs(event.dueAt, "YYYY-MM-DD").fromNow() + ")";
    }
  } else if (!!event.dueAt) {
    duePart = "Fällig am " + dayjs(event.dueAt, "YYYY-MM-DD").format("ddd, DD.MM.YYYY");
  }

  if (!!event.scheduledDate || !!event.scheduledAt) {
    const date = event.scheduledDate ? dayjs(event.scheduledDate) : dayjs.utc(event.scheduledAt).local();
    return ["Geplant für " + date.format("ddd, DD.MM.YYYY") + (event.scheduledDate ? " (ohne Uhrzeit)" : "")
      + (duePart.length > 0 ? " - " : "") + duePart, event.scheduledDate ?? event.scheduledAt];
  } else if (duePart.length > 0) {
    return [duePart, event.dueAt];
  }

  return ["Erstellt am " + dayjs(event.createdAt, "YYYY-MM-DD").format("ddd, DD.MM.YYYY"), event.createdAt];
}

/**
 * =========================
 * Row type (only what the table needs)
 * =========================
 */
type ServiceRow = {
  id: number;

  category: BobEvent["category"];
  title: string;

  priority: BobEvent["priority"];

  resourceNumber: string;
  resourceName: string;
  lastBookingText: string;
  lastBookingNumber: string;
  lastBookingErfasser: string;
  lastBookingErfasserPhone: string | null;
  createdBy: string;
  createdByPhone: string | null;

  bucketKey: "overdue" | "current" | "history";
  statusText: string;

  createdAt: Date | null;
  dueAt: Date | null;
  scheduledAt: Date | null;
  scheduledDate: string | null;
  completedAt: Date | null;

  // sort-only fields (keine Column)
  sortTime: number;
  completed: boolean;

  messageCount: number;
};

const bucketOrder: Record<ServiceRow["bucketKey"], number> = {
  overdue: 0,
  current: 1,
  history: 2,
};

const bucketLabels: Record<ServiceRow["bucketKey"], string> = {
  overdue: "Überfällig",
  current: "Offen",
  history: "Historie",
};

const categoryLabels: Record<BobEvent["category"], string> = {
  external: "Extern",
  meterReading: "Zählerstand",
  testing: "Regelmäßige Wartung",
  maintenance: "Prüfung/Inspektion",
  damage: "Mangel",
};

const prioLabels: Record<BobEvent["priority"], string> = {
  high: "Hoch",
  normal: "Normal",
  low: "Niedrig",
};

function getPriorityIcon(priority: BobEvent["priority"]) {
  if (priority === "high") return ChevronsUp;
  if (priority === "low") return ChevronsDown;
  return CircleDot;
}

function getCategoryIcon(category: BobEvent["category"]) {
  switch (category) {
    case "external":
      return OpenInNew;
    case "meterReading":
      return AccessTime;
    case "testing":
      return Checklist;
    case "maintenance":
      return VerifiedUser;
    case "damage":
      return Healing;
    default:
      return OpenInNew;
  }
}

function getBucketIcon(bucketKey: ServiceRow["bucketKey"]) {
  if (bucketKey === "overdue") return Warning;
  if (bucketKey === "current") return AccessTime;
  return EventAvailable;
}

function columnGetter(onOpen: (id: number) => void, showByNumber: (number: string) => void): GridColDef<ServiceRow>[] {
  return [
    {
      field: "category",
      headerName: "Art",
      type: "singleSelect",
      flex: 0.75,
      minWidth: 160,
      display: "flex",
      valueOptions: Object.keys(categoryLabels).map((k) => ({
        value: k,
        label: categoryLabels[k as BobEvent["category"]],
      })),
      valueGetter: (_value, row) => row.category,
      valueFormatter: (value) => categoryLabels[value as BobEvent["category"]] ?? String(value ?? ""),
      renderCell: (params) => {
        const cat = params.value as BobEvent["category"];
        const Icon = getCategoryIcon(cat);
        return (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Icon fontSize="small" />
            <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
              {categoryLabels[cat] ?? cat}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: "title",
      headerName: "Titel",
      type: "string",
      flex: 1.2,
      minWidth: 220,
      valueGetter: (_value, row) => row.title,
    },
    {
      field: "device",
      headerName: "Gerät",
      type: "string",
      flex: 1.3,
      minWidth: 240,
      valueGetter: (_value, row) => `${row.resourceNumber} ${row.resourceName}`.trim(),
      display: "flex",
      renderCell: (params) => {
        const number = params.row.resourceNumber;
        const name = params.row.resourceName;

        return (
          <Stack
            direction="row"
            spacing={1}
            alignItems="baseline"
            sx={{ minWidth: 0 }}
            className="dim_hover"
            onClick={() => showByNumber(params.row.resourceNumber)}
          >
            <span className="small_number">{number}</span>
            <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
              {name}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: "lastBooking",
      headerName: "Letzter Ort",
      type: "string",
      flex: 1.3,
      minWidth: 240,
      valueGetter: (_value, row) => `${row.lastBookingText}`.trim(),
      display: "flex",
      renderCell: (params) => {
        const hasContent = (params.row.lastBookingText ?? "").trim().length > 0;
        if (!hasContent) return "-";

        return (
          <Stack direction="row" spacing={1} alignItems="baseline" sx={{ minWidth: 0 }}>
            {params.row.lastBookingNumber ? <span className="small_number">{params.row.lastBookingNumber}</span> : null}
            <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
              {params.row.lastBookingText.replace(params.row.lastBookingNumber || "", "").trim()}
            </Typography>
            {params.row.lastBookingErfasser ? <span className="small_number">(<ContactName name={params.row.lastBookingErfasser} phone={params.row.lastBookingErfasserPhone} />)</span> : null}
          </Stack>
        );
      },
    },
    {
      field: "createdBy",
      headerName: "Ersteller",
      type: "string",
      flex: 1.3,
      minWidth: 240,
      valueGetter: (_value, row) => row.createdBy.trim(),
      display: "flex",
      renderCell: (params) => <ContactName name={params.row.createdBy} phone={params.row.createdByPhone} />,
    },
    {
      field: "priority",
      headerName: "Prio",
      type: "singleSelect",
      minWidth: 90,
      flex: 0.5,
      align: "center",
      headerAlign: "center",
      display: "flex",
      valueOptions: (Object.keys(prioLabels) as Array<BobEvent["priority"]>).map((k) => ({
        value: k,
        label: prioLabels[k],
      })),
      valueGetter: (_value, row) => row.priority,
      valueFormatter: (value) => prioLabels[value as BobEvent["priority"]] ?? String(value ?? ""),
      renderCell: (params) => {
        const p = params.value as BobEvent["priority"];
        const Icon = getPriorityIcon(p);
        const isHigh = p === "high";

        return (
          <Tooltip title={prioLabels[p] ?? String(p)}>
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: isHigh ? "error.main" : "transparent",
                color: isHigh ? "error.contrastText" : "inherit",
              }}
            >
              <Icon size={16} />
            </Box>
          </Tooltip>
        );
      },
      sortComparator: (a, b) => {
        const order: Record<BobEvent["priority"], number> = { high: 0, normal: 1, low: 2 };
        const aa = order[a as BobEvent["priority"]] ?? 999;
        const bb = order[b as BobEvent["priority"]] ?? 999;
        return aa - bb;
      },
    },
    {
      field: "bucketKey",
      headerName: "Status",
      type: "singleSelect",
      flex: 1.4,
      minWidth: 260,
      display: "flex",
      valueOptions: (Object.keys(bucketLabels) as Array<ServiceRow["bucketKey"]>).map((k) => ({
        value: k,
        label: bucketLabels[k],
      })),
      valueGetter: (_value, row) => row.bucketKey,
      valueFormatter: (value) => bucketLabels[value as ServiceRow["bucketKey"]] ?? String(value ?? ""),
      renderCell: (params) => {
        const key = params.row.bucketKey;
        const Icon = getBucketIcon(key);
        return (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Icon fontSize="small" />
            <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
              {params.row.statusText}
            </Typography>
          </Stack>
        );
      },
      sortComparator: (a, b, cellA, cellB) => {
        const ao = bucketOrder[a as ServiceRow["bucketKey"]] ?? 999;
        const bo = bucketOrder[b as ServiceRow["bucketKey"]] ?? 999;
        if (ao !== bo) return ao - bo;

        const ra = (cellA.api.getRow(cellA.id) as ServiceRow | undefined) ?? undefined;
        const rb = (cellB.api.getRow(cellB.id) as ServiceRow | undefined) ?? undefined;

        const ta = ra?.sortTime ?? Number.NEGATIVE_INFINITY;
        const tb = rb?.sortTime ?? Number.NEGATIVE_INFINITY;

        const ca = ra?.completed ?? false;
        const cb = rb?.completed ?? false;

        if (ca || cb) return tb - ta;
        return ta - tb;
      },
    },
    {
      field: "createdAt",
      headerName: "Erstellt",
      type: "dateTime",
      minWidth: 190,
      valueGetter: (_value, row) => row.createdAt,
    },
    {
      field: "dueAt",
      headerName: "Fällig",
      type: "date",
      minWidth: 160,
      valueGetter: (_value, row) => row.dueAt,
    },
    {
      field: "scheduledAt",
      headerName: "Geplant",
      type: "dateTime",
      minWidth: 190,
      valueGetter: (_value, row) => row.scheduledAt,
      valueFormatter: (value, row) => row.scheduledDate
        ? dayjs(row.scheduledDate).format("DD.MM.YYYY")
        : value instanceof Date ? value.toLocaleString("de-DE") : "",
    },
    {
      field: "completedAt",
      headerName: "Erledigt",
      type: "dateTime",
      minWidth: 190,
      valueGetter: (_value, row) => row.completedAt,
    },
    {
      field: "Aktionen",
      headerName: "",
      width: 120,
      sortable: false,
      filterable: false,
      disableColumnMenu: false,
      display: "flex",
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ width: "100%" }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <ChatBubbleOutline fontSize="small" />
            <Box component="span">{params.row.messageCount}</Box>
          </Stack>
          <Tooltip title="Öffnen">
            <IconButton size="small" onClick={() => onOpen(params.row.id)}>
              <ArrowForward fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];
}

/**
 * =========================
 * Component
 * =========================
 */
export default function Service() {
  const setPageTitle = useGlobalStore((state) => state.setPageTitle);
  useEffect(() => setPageTitle("Service"), [setPageTitle]);

  const region = usePersistantStore((store) => store.region);

  const rights = useRights();
  const role: "site" | "service" = "SERVICE" in rights ? "service" : "site";

  const apiRef = useGridApiRef();

  const [sidebarId, setSidebarId] = useState<number | null>(null);
  const sidebarOpen = sidebarId !== null;

  type ServiceFilter = "all" | "damage" | "nonDamage";
  const [filter, setFilter] = useState<ServiceFilter>("all");

  const handleFilterChange = (_: React.MouseEvent<HTMLElement>, next: ServiceFilter | null) => {
    if (next) setFilter(next);
  };

  const handleOpenSidebar = (id: number) => {
    setSidebarId(id);
  };
  const handleCloseSidebar = () => {
    setSidebarId(null);
  };

  const inFourWeeks = dayjs().add(4, "week").endOf("day").format("YYYY-MM-DD");

  const maengel = {
    orderOps: [],
    sortModel: [
      { sort: "asc", field: "priority" },
      { sort: "asc", field: "bucketKey" },
    ],
    filterModel: {
      items: [
        { id: 35837, field: "category", value: "damage", operator: "is" },
        { id: 76785, field: "bucketKey", value: "history", operator: "not" },
      ],
    },
    columnWidths: [],
    pinnedColumns: { left: [], right: ["Aktionen"] },
    columnVisibilityModel: {
      category: false,
      createdAt: false,
      dueAt: false,
      scheduledAt: false,
      completedAt: false,
    },
  };

  const termine = {
    orderOps: [],
    sortModel: [
      { sort: "asc", field: "priority" },
      { sort: "asc", field: "bucketKey" },
    ],
    filterModel: {
      items: [
        { id: 51119, field: "category", value: "damage", operator: "not" },
        { id: 51561, field: "bucketKey", value: "history", operator: "not" },
        { id: 51562, field: "dueAt", value: inFourWeeks, operator: "onOrBefore" },
      ],
    },
    columnWidths: [],
    pinnedColumns: { left: [], right: ["Aktionen"] },
    columnVisibilityModel: {
      createdAt: false,
      dueAt: false,
      scheduledAt: false,
      completedAt: false,
    },
  };

  const { showByNumber } = useDeviceInfoModal();

  const { gridProps } = usePersistentDataGrid("serviceoverview", columnGetter(handleOpenSidebar, showByNumber), maengel, [
    {
      name: "Offene M\u00e4ngel",
      starred: true,
      state: maengel,
    },
    {
      name: "Termine",
      starred: true,
      state: termine,
    },
  ]);

  const eventQuery = useQuery<BobEvent[], any, BobEvent[]>({
    queryKey: ["getEvents", region],
    queryFn: apiService.getServiceList(region),
  });

  const rows = useMemo<ServiceRow[]>(() => {
    const dataRaw = eventQuery.data ?? [];

    const data =
      filter === "all"
        ? dataRaw
        : filter === "damage"
          ? dataRaw.filter((e) => e.category === "damage")
          : dataRaw.filter((e) => e.category !== "damage");

    const defs = getBinDefinitons(role);
    const bins = generateBins(data, defs);

    const out: Array<{ row: ServiceRow; bucketIdx: number; t: number; completed: boolean }> = [];

    bins.forEach((bin) => {
      const bucketIdx = bucketOrder[bin.key];

      bin.events.forEach((ev) => {
        const lastBooking = ev.resource.lastBooking;

        let lastBookingNumber = "";
        let lastBookingText = "";
        let lastBookingErfasser = "";
        let lastBookingErfasserPhone: string | null = null;

        if (lastBooking) {
          const [kind, payload] = lastBooking;
          if (kind === "rb") {
            lastBookingNumber = payload?.job?.phase?.project?.number ?? "";
            const name = payload?.job?.phase?.project?.name ?? "";
            lastBookingText = `${lastBookingNumber} ${name}`.trim();
            lastBookingErfasser = payload?.erfasser?.name ?? "";
            lastBookingErfasserPhone = payload?.erfasser?.phone ?? null;
          } else if (kind === "spb") {
            lastBookingNumber = "";
            lastBookingText = `${payload?.specialPlace?.name ?? ""}`.trim();
          } else {
            lastBookingNumber = payload?.project?.number ?? "";
            const name = payload?.project?.name ?? "";
            lastBookingText = `${lastBookingNumber} ${name}`.trim();
            lastBookingErfasser = payload?.erfasser?.name ?? "";
            lastBookingErfasserPhone = payload?.erfasser?.phone ?? null;
          }
        }

        const t = eventTimeMs(ev);
        const completed = !!ev.completedAt;

        const [statusText] = getSubtitle(ev);

        out.push({
          bucketIdx,
          t,
          completed,
          row: {
            id: ev.id,
            category: ev.category,
            title: ev.title || ev.categorySettings?.title || "",
            priority: ev.priority,
            resourceNumber: ev.resource.number ?? "",
            resourceName: ev.resource.name ?? "",
            lastBookingText,
            lastBookingNumber,
            lastBookingErfasser,
            lastBookingErfasserPhone,
            bucketKey: bin.key,
            statusText,
            createdBy: !!ev.createdBy ? ev.createdBy.name + ", " + ev.createdBy.firstName : "",
            createdByPhone: ev.createdBy?.phone ?? null,
            createdAt: toDateTimeValue(ev.createdAt),
            dueAt: toDateOnlyValue(ev.dueAt),
            scheduledAt: ev.scheduledDate ? toDateOnlyValue(ev.scheduledDate) : toDateTimeValue(ev.scheduledAt),
            scheduledDate: ev.scheduledDate ?? null,
            completedAt: toDateTimeValue(ev.completedAt),
            sortTime: t,
            completed,
            messageCount: ev.messageCount ?? 0,
          },
        });
      });
    });

    out.sort((a, b) => {
      if (a.bucketIdx !== b.bucketIdx) return a.bucketIdx - b.bucketIdx;
      if (a.completed) return b.t - a.t;
      return a.t - b.t;
    });

    return out.map((x) => x.row);
  }, [eventQuery.data, role, filter]);

  return (
    <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", p: 0, background: "#eee" }}>
        <TopBar>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
            <AddEvent onCreate={setSidebarId} />

            <Box sx={{ flex: 1 }} />
          </Stack>
        </TopBar>

        <Paper sx={{ overflow: "hidden", display: "flex", m: 1, flexDirection: "column" }}>
          <DataGridPro<ServiceRow>
            rows={rows}
            apiRef={apiRef}
            rowHeight={36}
            getRowId={(row) => row.id}
            loading={eventQuery.isLoading || eventQuery.isFetching}
            showToolbar
            {...gridProps}
          />
        </Paper>
      </Box>

      <Drawer
        anchor="right"
        open={sidebarOpen}
        onClose={handleCloseSidebar}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 700 },
            maxWidth: "100vw",
          },
        }}
      >
        {sidebarId === null ? null : (
          <EventSidebar
            dateOnlyScheduling
            allowMessageDeletion={"DISPOSITION" in rights}
            id={sidebarId}
            apiService={ApiService}
            onExportPdf={async snapshot => {
              const { exportTicketPdf } = await import("./ticketPdf");
              return exportTicketPdf(snapshot, async attachment => new Uint8Array(await ApiService.getTicketAttachment(snapshot.id, attachment.messageId, attachment.index)));
            }}
            onCreate={setSidebarId}
            onClose={handleCloseSidebar}
            deviceNumber={0}
            fullAppRights={"DISPOSITION" in rights || "SERVICE" in rights}
            role={"SERVICE" in rights ? "service" : "site"}
          />
        )}
      </Drawer>
    </Box>
  );
}
